import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminAPI, authAPI, contentAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Loader } from '@/components/ui/loader';

export const AdminPartners = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    designation: { en: '', hi: '' },
    bio: { en: '', hi: '' },
    image_url: '',
    order: 0
  });

  const checkAuth = useCallback(async () => {
    try {
      await authAPI.getMe();
      fetchPartners();
    } catch (error) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const fetchPartners = async () => {
    try {
      const res = await contentAPI.getPartners();
      setPartners(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await adminAPI.updatePartner(editing, formData);
        toast.success('Partner updated');
      } else {
        await adminAPI.createPartner(formData);
        toast.success('Partner created');
      }
      resetForm();
      fetchPartners();
    } catch (error) {
      toast.error('Error saving partner');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      designation: { en: '', hi: '' },
      bio: { en: '', hi: '' },
      image_url: '',
      order: 0
    });
    setEditing(null);
  };

  const handleEdit = (partner) => {
    setFormData(partner);
    setEditing(partner.name);
  };

  const handleDelete = async (name) => {
    if (window.confirm('Delete this partner?')) {
      try {
        await adminAPI.deletePartner(name);
        toast.success('Partner deleted');
        fetchPartners();
      } catch (error) {
        toast.error('Error deleting');
      }
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100"><Loader className="text-secondary" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 admin-panel">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard"><Button variant="ghost" size="sm"><ArrowLeft size={18} className="mr-2" />Back</Button></Link>
            <h1 className="text-2xl font-bold">Manage Partners</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-6">{editing ? 'Edit' : 'Add'} Partner</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} disabled={editing} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Designation (English)</label>
                <Input value={formData.designation.en} onChange={(e) => setFormData({ ...formData, designation: { ...formData.designation, en: e.target.value } })} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Designation (Hindi)</label>
                <Input value={formData.designation.hi} onChange={(e) => setFormData({ ...formData, designation: { ...formData.designation, hi: e.target.value } })} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bio (English)</label>
                <Textarea value={formData.bio.en} onChange={(e) => setFormData({ ...formData, bio: { ...formData.bio, en: e.target.value } })} required rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bio (Hindi)</label>
                <Textarea value={formData.bio.hi} onChange={(e) => setFormData({ ...formData, bio: { ...formData.bio, hi: e.target.value } })} required rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Image URL (optional)</label>
                <Input value={formData.image_url || ''} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Order</label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} required />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1">{editing ? 'Update' : 'Create'}</Button>
                {editing && <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>}
              </div>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-6">Partners List</h2>
            <div className="space-y-4">
              {partners.map((partner) => (
                <div key={partner.name} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{partner.name}</h3>
                      <p className="text-sm text-gray-600">{partner.designation.en}</p>
                      <p className="text-xs text-gray-500 mt-1">{partner.bio.en.substring(0, 80)}...</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(partner)}><Edit size={16} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(partner.name)} className="text-red-600"><Trash2 size={16} /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};